import { createFileRoute, Link } from "@tanstack/react-router";
import { ArticleLayout } from "@/components/ArticleLayout";
import { ogImageFor, ogMetaTags, twitterTextTags } from "@/lib/ogImages";
import { getArticleBySlug } from "@/lib/learningHub";

const SITE_URL = "https://coloradowebimpressions.com";
const PATH = "/learn/aeo-geo-aio-explained";
const ARTICLE = getArticleBySlug(PATH);
const OG_IMAGE = `${SITE_URL}${ogImageFor({ articleCategory: ARTICLE?.category })}`;

export const Route = createFileRoute("/learn/aeo-geo-aio-explained")({
  head: () => ({
    meta: [
      { title: "AEO, GEO, AIO, SGE: What All These New SEO Acronyms Actually Mean" },
      {
        name: "description",
        content:
          "Plain-English glossary of AEO, GEO, AIO, and SGE — ranked by what actually matters for a Colorado small business in 2026 (and which ones you can safely ignore).",
      },
      { property: "og:title", content: "AEO vs GEO vs AIO: A Plain-English Guide for Colorado Businesses" },
      {
        property: "og:description",
        content:
          "Vendors are stacking acronyms to sell you 'new' services. Here's which ones are real, which overlap, and which you can ignore.",
      },
      { property: "og:url", content: `${SITE_URL}${PATH}` },
      { property: "og:type", content: "article" },
      ...ogMetaTags(OG_IMAGE),
      ...twitterTextTags({
        title: ARTICLE?.title ?? "AEO, GEO, AIO explained",
        description: ARTICLE?.desc ?? "What all the new SEO acronyms actually mean — and which matter.",
      }),
    ],
    links: [{ rel: "canonical", href: `${SITE_URL}${PATH}` }],
  }),
  component: Article,
});

function Article() {
  return (
    <ArticleLayout
      eyebrow="AI & GEO"
      read="7 min read"
      title="AEO, GEO, AIO, SGE — what all these new SEO acronyms actually mean (and which matter)"
      intro="Every week somebody emails me asking about a new three-letter acronym their last agency invoiced them for. Here's the cheat sheet — what each one really is, where they overlap, and which you can safely ignore."
      breadcrumbs={[
        { label: "Home", to: "/" },
        { label: "Learning Hub", to: "/learn" },
        { label: "AEO, GEO, AIO explained" },
      ]}
      path={PATH}
      faqs={[
        { q: "What's the difference between SEO, AEO, GEO, and AIO?", a: "SEO is ranking on a results page. AEO (Answer Engine Optimization) is being chosen as the direct answer in featured snippets, voice search, and AI Overviews. GEO (Generative Engine Optimization) is being cited inside answers from generative AI tools like ChatGPT, Perplexity, and Gemini. AIO is mostly a marketing rebrand of GEO. SGE was Google's preview name for what's now called AI Overviews." },
        { q: "Should I pay extra for an 'AEO' or 'GEO' service?", a: "Maybe — but ask the agency to show you exactly what schema they're adding, which AI tools are currently citing your business, and how they measure progress. If they can't answer those three questions concretely, the acronym is marketing fluff." },
        { q: "Is anyone really getting 'guaranteed ChatGPT placement'?", a: "No. Anyone selling that is lying. LLMs do not have a paid placement system or a deterministic ranking algorithm you can guarantee against. You can only invest in the structural work that makes citation more likely." },
      ]}
    >
      <h2>The acronyms, plain-English</h2>

      <h3>SEO — Search Engine Optimization</h3>
      <p>The original. Optimizing your site to rank in traditional search engine results pages (Google, Bing). Still the foundation. Not going anywhere for local intent.</p>

      <h3>AEO — Answer Engine Optimization</h3>
      <p>
        Optimizing to be chosen as the <em>direct answer</em>: featured snippets, voice search results, "People Also Ask" boxes, and
        Google AI Overviews. The deliverables are mostly: clear question-and-answer formatting, FAQ schema, structured headings, and
        concise definitional paragraphs. About 80% of "AEO" is just SEO done well with a focus on direct answers.
      </p>

      <h3>GEO — Generative Engine Optimization</h3>
      <p>
        The newer one. Optimizing to be <em>cited</em> by generative AI tools — ChatGPT, Perplexity, Gemini, Claude, Copilot — when they
        answer a customer's question. The deliverables: entity clarity (knowledge graph signals), high-authority citations, structured
        data (LocalBusiness, Service, FAQ, Review), and content the LLMs find quotable. This is real. It overlaps heavily with AEO and
        traditional SEO.
      </p>

      <h3>AIO — AI Optimization</h3>
      <p>
        Mostly a marketing rebrand of GEO. Some agencies use it as an umbrella for AEO + GEO + AI Overviews work. There's no
        meaningful technical difference between a properly-scoped GEO program and an AIO program. If a vendor is charging you for both
        as separate line items, they're double-billing.
      </p>

      <h3>SGE — Search Generative Experience</h3>
      <p>
        This was Google's internal/preview name for what publicly launched as <strong>AI Overviews</strong>. If you see "SGE" on
        somebody's pitch deck in 2026, they wrote that deck a long time ago. Same thing, current name.
      </p>

      <h2>Ranked by what actually matters for a Colorado small business</h2>
      <ol>
        <li>
          <strong>Local SEO + Google Business Profile.</strong> Still #1 for any business where someone calls you. Read the{" "}
          <Link to="/learn/local-seo-guide" className="font-bold text-brand-gold-deep underline">local SEO guide</Link>.
        </li>
        <li><strong>AEO basics</strong> — FAQ schema, clear direct answers, well-structured pages. Cheap, fast, high payoff.</li>
        <li>
          <strong>GEO foundations</strong> — entity cleanup, schema layering, authoritative citations. The new layer that matters in 2026.
          See{" "}
          <Link to="/learn/ai-search-geo" className="font-bold text-brand-gold-deep underline">our GEO explainer</Link>.
        </li>
        <li><strong>Reviews</strong> — still feed both Google and the LLMs. Underrated as an AI-citation signal.</li>
        <li>
          <strong>"AIO" / "Answer-engine packages"</strong> — usually fine if the deliverables are real. Ask for the specifics.
        </li>
      </ol>

      <h2>What to ignore</h2>
      <ul>
        <li>"Guaranteed ChatGPT placement." Not a thing. Run.</li>
        <li>"AI-written content packages" with no human editing. Both Google and the LLMs penalize obviously generated thin content.</li>
        <li>Any vendor inventing a fifth acronym this quarter to justify a price increase. The acronyms are settling. The work isn't really new — it's good SEO with structural emphasis on direct answers and entity clarity.</li>
      </ul>

      <h2>The honest bottom line</h2>
      <p>
        For 95% of Colorado small businesses, the right move in 2026 is: keep doing local SEO, add structured FAQ + answer formatting,
        clean up your entity and citations, and monitor whether AI tools are naming you. That covers AEO, GEO, AIO, and AI Overviews
        without paying four invoices.
      </p>
      <p>
        If you want a snapshot of where you actually stand right now,{" "}
        <Link to="/ai-visibility-audit" className="font-bold text-brand-gold-deep underline">the AI Visibility Audit</Link>{" "}
        runs real prompts in ChatGPT, Perplexity, Gemini, and Google AI Overviews and gives you a 90-day roadmap. No acronym soup.
      </p>
    </ArticleLayout>
  );
}
